import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useToast } from "../../hooks/use-toast";
import { useAuth } from "../../context/AuthContext";
import { useNotifications } from "../../context/NotificationContext";
import { createBooking } from "../../api/api";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../ui/form";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Textarea } from "../ui/textarea";
import { formatCurrency } from "../../lib/utils";
import { Restaurant } from "../../types";

interface BookingFormProps {
  restaurant: Restaurant;
}

const formSchema = z.object({
  date: z.string().min(1, { message: "Date is required" }),
  time: z.string().min(1, { message: "Time is required" }),
  guests: z.string().min(1, { message: "Number of guests is required" }),
  dietary: z.string().optional(),
});

type BookingFormValues = z.infer<typeof formSchema>;

const BookingForm = ({ restaurant }: BookingFormProps) => {
  const { isAuthenticated } = useAuth();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<BookingFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      date: "",
      time: "",
      guests: "2",
      dietary: "",
    },
  });

  const guests = parseInt(form.watch("guests") || "2");
  const totalPrice = restaurant.price * guests;

  const onSubmit = async (data: BookingFormValues) => {
    if (!isAuthenticated) {
      toast({
        title: "Authentication required",
        description: "Please login to book a surprise meal",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const booking = await createBooking({
        restaurantId: restaurant.id,
        ...data,
        guests: data.guests,
      });

      toast({
        title: "Booking confirmed!",
        description: `Your surprise meal at ${restaurant.name} has been booked`,
        variant: "default",
      });

      form.reset();
    } catch (error) {
      toast({
        title: "Booking failed",
        description: "Please try again later",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <h3 className="mb-4 text-lg font-semibold text-gray-800 dark:text-white">
          Book Your Surprise
        </h3>

        <div className="space-y-4">
          <FormField
            control={form.control}
            name="date"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Date</FormLabel>
                <FormControl>
                  <Input
                    type="date"
                    min={new Date().toISOString().split("T")[0]}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="time"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Time</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a time" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="18:00">6:00 PM</SelectItem>
                    <SelectItem value="18:30">6:30 PM</SelectItem>
                    <SelectItem value="19:00">7:00 PM</SelectItem>
                    <SelectItem value="19:30">7:30 PM</SelectItem>
                    <SelectItem value="20:00">8:00 PM</SelectItem>
                    <SelectItem value="20:30">8:30 PM</SelectItem>
                    <SelectItem value="21:00">9:00 PM</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="guests"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Number of Guests</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select number of guests" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="1">1 Person</SelectItem>
                    <SelectItem value="2">2 People</SelectItem>
                    <SelectItem value="3">3 People</SelectItem>
                    <SelectItem value="4">4 People</SelectItem>
                    <SelectItem value="5">5 People</SelectItem>
                    <SelectItem value="6">6 People</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="dietary"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Dietary Preferences</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Let us know about any allergies, restrictions, or preferences..."
                    rows={3}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="border-t border-gray-200 pt-4 dark:border-gray-700">
            <div className="mb-2 flex justify-between">
              <span className="text-gray-600 dark:text-gray-300">
                Price per person
              </span>
              <span className="font-medium text-gray-800 dark:text-white">
                {formatCurrency(restaurant.price)}
              </span>
            </div>
            <div className="mb-2 flex justify-between">
              <span className="text-gray-600 dark:text-gray-300">
                Number of guests
              </span>
              <span className="font-medium text-gray-800 dark:text-white">
                {guests}
              </span>
            </div>
            <div className="flex justify-between font-bold">
              <span className="text-gray-800 dark:text-white">Total</span>
              <span className="text-gray-800 dark:text-white">
                {formatCurrency(totalPrice)}
              </span>
            </div>
          </div>

          <Button
            type="submit"
            className="w-full bg-primary py-3 font-medium text-white hover:bg-red-700"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Processing..." : "Confirm Booking"}
            {!isSubmitting && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="ml-2 h-4 w-4"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default BookingForm;
